import React, { useMemo } from 'react';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import EntriesInfo from './EntriesInfo';
import PageSizeSelector from './PageSizeSelector';

interface PaginationProps {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  showEntriesInfo?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  totalItems,
  currentPage,
  pageSize,
  defaultPageSize = 10,
  pageSizeOptions = [5, 10, 20, 50],
  onPageChange,
  onPageSizeChange,
  showEntriesInfo = false,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const getVisiblePages = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 6) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, '...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages;
  };

  const visiblePages = useMemo(() => getVisiblePages(), [currentPage, totalPages]);

  // if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col sm:flex-row justify-between sm:items-center py-3 sm:gap-4">
      {/* Page Size Selector */}
      <div className="flex items-center gap-2 pl-1 sm:pl-0">
        {showEntriesInfo ? (
          <EntriesInfo currentPage={currentPage} pageSize={pageSize} totalItems={totalItems} />
        ) : (
          <PageSizeSelector
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
            onPageSizeChange={onPageSizeChange}
          />
        )}
      </div>

      {/* Page Navigation */}
      <div className="flex items-center divide-x divide-[#FEC733] border border-[#FEC733] bg-[#DEE2E6] overflow-hidden w-fit">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center justify-center h-8 w-8  text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-black/5"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>

        {visiblePages.map((page, i) =>
          page === '...' ? (
            <span
              key={`ellipsis-${i}`}
              className="flex items-center justify-center h-8 w-8 text-gray-400 select-none"
            >
              ...
            </span>
          ) : (
            <button
              key={`page-${page}`}
              type="button"
              onClick={() => onPageChange(page as number)}
              className={`flex items-center justify-center h-8 w-8 text-sm font-medium ${
                page === currentPage ? 'bg-[#FEC733] text-white' : 'text-gray-600 hover:bg-black/5'
              }`}
            >
              {page}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center justify-center h-8 w-8 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-black/5"
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
